import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	NodeChangeType,
	ApolloQueryErrorMapper,
	SubscriptionManagerService,
	CombiningSubscriptionManager,
	ApolloQueryError
} from '@shure/shared/angular/data-access/system-api/core';
import { ChannelDeverbIntensity, Response, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers } from '@shure/shared/typescript/rxjs';

import { DeverbApiService, DeverbChannel } from '../../api';
import {
	DeverbChananelSubscriptionGQL,
	DeverbChannelMutationGQL,
	DeverbChannelQueryGQL
} from '../graphql/generated/deverb.generated';
import { mapDeverbChannelFromSysApi } from '../mappers';

@Injectable()
export class DaiDeviceUiDspDeverbService implements DeverbApiService {
	private readonly logger: ILogger;

	private readonly deverbChannelSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly deverbChannelQueryGQL: DeverbChannelQueryGQL,
		private readonly deverbChannelMutationGQL: DeverbChannelMutationGQL,
		deverbChannelSubscriptionGQL: DeverbChananelSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiDeviceUiDspDeverbService');

		this.deverbChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspDeverbService',
				changeTypes: [NodeChangeType.AudioChannelDeverb, NodeChangeType.AudioChannelChannelConfiguration],
				subscriptionGql: deverbChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getChannel$(channelId: string): Observable<DeverbChannel> {
		const queryId = uuidv4();
		this.logger.trace('getChannel$()', 'Querying deverb channel', { channelId, queryId });

		return this.deverbChannelQueryGQL.watch({ channelId }, { fetchPolicy: 'network-only' }).valueChanges.pipe(
			map((query) => {
				if (query.data?.node && 'features' in query.data.node) {
					this.logger.trace('getChannel$()', 'Query updated', { channelId });
					return mapDeverbChannelFromSysApi(query.data.node);
				}

				this.logger.error('getChannel$()', 'Failed to query channel', { channelId });
				throw ApolloQueryErrorMapper.getError(query);
			}),
			tap((channenl) => this.deverbChannelSubscriptionManager.subscribe(queryId, channenl.id)),
			noMoreSubscribers(() => {
				this.logger.trace('getChannel$()', 'noMoreSubscribers', { channelId, queryId });
				this.deverbChannelSubscriptionManager.unsubscribeQuery(queryId);
			}),
			catchError((error) => {
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public toggleDeverb(channelId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleDeverb()', 'Toggling deverb', { channelId, enabled });
		return this.updateChannel(channelId, { enabled });
	}
	public setDeverbIntensity(
		channelId: string,
		intensity: ChannelDeverbIntensity
	): Observable<Response<void, string>> {
		this.logger.trace('setDeverbIntensity()', 'Setting deverb intensity level', { channelId, intensity });
		return this.updateChannel(channelId, { intensity });
	}

	private updateChannel(
		channelId: string,
		update: Partial<DeverbChannel['features']['deverb']>
	): Observable<Response<void, string>> {
		return this.deverbChannelMutationGQL
			.mutate(
				{
					updates: {
						audioChannel: {
							id: channelId,
							features: {
								deverb: {
									...update
								}
							}
						}
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateNodes &&
						'error' in mutation.data.updateNodes[0] &&
						mutation.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update channel', {
							channelId,
							mutation
						});
						return { state: ResponseState.Error, error: mutation.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateChannel()', 'Failed to update channel', { error });
					return of({ state: ResponseState.Error, error });
				})
			);
	}
}
